<script lang="ts">
	import PlusMinusButton from "@components/PlusMinusButton.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue, EventHandler } from "svelte/elements";
	import cross from "@core/assets/icons/close.svg?raw";

	interface NumberInputProps {
		name?: string;
		id?: string;
		min?: number;
		max?: number;
		value?: number;
		required?: boolean;
		inputClass?: ClassValue;
		class?: ClassValue;
		disabled?: boolean;
		input?: HTMLInputElement | null;
		oninput?(this: void, value: number): void;
	}

	let {
		input = $bindable(null),
		/* eslint-disable prefer-const */
		value,
		id,
		name = id,
		min = -Infinity,
		max = Infinity,
		required = false,
		inputClass = "",
		class: customClass = "",
		disabled = false,

		oninput: onInputCallback,
		/* eslint-enable prefer-const */
	}: NumberInputProps = $props();

	let holdTimeout: number | undefined = undefined;
	const holdIntervalSpeed = 250;
	const minimalIntervalSpeed = 50;
	const intervalSpeedDrop = 15;

	function stopHold(): void {
		clearTimeout(holdTimeout);
		holdTimeout = undefined;
	}

	function increment(): void {
		if (disabled) {
			return;
		}
		const newValue = value === undefined ? 1 : value + 1;
		if (newValue <= max) {
			onInputCallback?.(newValue);
		}
	}

	function decrement(): void {
		if (disabled) {
			return;
		}
		const newValue = value === undefined ? 1 : value - 1;
		if (newValue >= min) {
			onInputCallback?.(newValue);
		}
	}

	function startDecrementing(speed = holdIntervalSpeed): void {
		stopHold();
		holdTimeout = window.setTimeout(() => {
			decrement();
			startDecrementing(Math.max(minimalIntervalSpeed, speed - intervalSpeedDrop));
		}, speed);
	}

	function startIncrementing(speed = holdIntervalSpeed): void {
		stopHold();
		holdTimeout = window.setTimeout(() => {
			increment();
			startIncrementing(Math.max(minimalIntervalSpeed, speed - intervalSpeedDrop));
		}, speed);
	}

	$effect(() => {
		if (input) {
			const valueIsOutOfBounds =
				typeof value === "number" &&
				((min !== -Infinity && Number(value) < min) || (max !== Infinity && Number(value) > max));

			if (typeof value === "number" && required) {
				input.setCustomValidity("Toto pole je povinné.");
			} else if (valueIsOutOfBounds) {
				if (min === -Infinity && max !== Infinity) {
					input.setCustomValidity(`Hodnota musí být maximálně ${max}.`);
				} else if (min !== -Infinity && max === Infinity) {
					input.setCustomValidity(`Hodnota musí být minimálně ${min}.`);
				} else if (min !== -Infinity && max !== Infinity) {
					input.setCustomValidity(`Hodnota musí být v rozmezí ${min} až ${max}.`);
				}
			} else {
				input.setCustomValidity("");
			}
		}
	});

	let focus = $state(false);

	const onblur: EventHandler<FocusEvent, HTMLInputElement> = (e) => {
		focus = false;

		onInputCallback?.(Number(e.currentTarget.value.replace(/[^0-9]/gu, "")));
	};
</script>

<svelte:window onmouseup={stopHold} />

<div class={[customClass, "flex items-center gap-1"]}>
	<PlusMinusButton
		kind="minus"
		type="button"
		class="peer"
		onclick={decrement}
		onmousedown={(): void => {
			startDecrementing();
		}}
		onblur={stopHold}
		{disabled}
	/>
	<input
		bind:this={input}
		class={[
			"text-center w-10 xl:w-31.5 grow outline-none text-base lg:text-md xl:text-title3 font-semibold peer-disabled:text-nov-light-300 block [appearance:none]",
			inputClass,
		]}
		type="text"
		inputmode="numeric"
		{id}
		{name}
		{disabled}
		value={focus ? value : `${value} ks`}
		{max}
		readonly={!focus}
		maxlength={focus && max !== Infinity ? String(max).length : undefined}
		onfocus={(): void => {
			focus = true;
		}}
		{onblur}
	/>
	<PlusMinusButton
		kind="plus"
		type="button"
		onclick={increment}
		onmousedown={(): void => {
			startIncrementing();
		}}
		onblur={stopHold}
		{disabled}
	>
		<Icon icon={cross} />
	</PlusMinusButton>
</div>
